import { useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { handleGetRequest, handlePatchRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import { formatTime } from '../../utils/date.util'
import { showToaster } from '../../utils/show-toaster.util'
import { useState, useEffect } from 'react'
import ConfirmModal from '../../components/common/ConfirmModal'
import ResolveReportModal from '../../components/admin/ResolveReportModal'
import { CONTENT_REPORT_STATUS } from '../../constants/supportTicket'
import UserTypeDisplay from '../../components/common/UserTypeDisplay'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import InfoGrid from '../../components/common/InfoGrid'
import ReportTicketStatus from '../../components/admin/ReportTicketStatus'
import DisplayReportedContent from '../../components/admin/DisplayReportedContent'

export default function ReportsTicketDetailsPage() {
  const { id } = useParams()
  const [showConfirm, setShowConfirm] = useState(false)
  const [showResolveModal, setShowResolveModal] = useState(false)

  const { data: reportDetails } = useQuery({
    queryKey: QueryKeys.adminReportTicketById(id),
    queryFn: () => handleGetRequest(`/admin/reports/${id}`),
    enabled: !!id,
  })

  const { report, content } = reportDetails?.data || {}

  useEffect(() => {
    if (report?.status === CONTENT_REPORT_STATUS.pending.key) {
      updateStatusMutation.mutate({ status: CONTENT_REPORT_STATUS.in_progress.key })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report?.status])

  const removeReportMutation = useMutation({
    mutationFn: () => handlePatchRequest(`/admin/reports/${id}/remove`),
    onSuccess: () => {
      showToaster('success', 'Report removed successfully')
      QueryKeys.adminReportsTickets.invalidateAll()
      QueryKeys.adminReportTicketById.invalidate(id)
      setShowConfirm(false)
    },
    onError: () => {
      showToaster('error', 'Failed to remove report')
    },
  })

  const reinstateReportMutation = useMutation({
    mutationFn: () => handlePatchRequest(`/admin/reports/${id}/reinstate`),
    onSuccess: () => {
      showToaster('success', 'Report reinstated successfully')
      QueryKeys.adminReportsTickets.invalidateAll()
      QueryKeys.adminReportTicketById.invalidate(id)
    },
    onError: () => {
      showToaster('error', 'Failed to reinstate report')
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: (payload) => handlePatchRequest(`/admin/reports/${id}/resolve`, payload),
    onSuccess: () => {
      QueryKeys.adminReportsTickets.invalidateAll()
      QueryKeys.adminReportTicketById.invalidate(id)
      setShowResolveModal(false)
    },
    onError: () => {
      showToaster('error', 'Failed to update report status')
    },
  })

  const renderActions = () => {
    if (report.contentRemainActiveReason) return

    const canReinstate =
      report.status === CONTENT_REPORT_STATUS.removed.key ||
      (report.status === CONTENT_REPORT_STATUS.resolved.key && !report.contentRemainActiveReason)

    if (canReinstate) {
      return (
        <div>
          <button
            className="bg-neutral-100 border border-button-border rounded px-6 py-2 text-black font-normal disabled:opacity-50"
            disabled={reinstateReportMutation.isLoading}
            onClick={async () => {
              await reinstateReportMutation.mutateAsync()
            }}
          >
            {reinstateReportMutation.isLoading ? 'Reinstating...' : 'Reinstate'}
          </button>
        </div>
      )
    }

    return (
      <>
        <button
          className="bg-error-600 text-white border border-button-border rounded-[4px] px-6 py-2 font-normal disabled:opacity-50"
          disabled={removeReportMutation.isLoading}
          onClick={() => setShowConfirm(true)}
        >
          {removeReportMutation.isLoading ? 'Dismissing...' : 'Dismiss'}
        </button>
        <button
          className="bg-neutral-100 border border-button-border rounded-[4px] px-6 py-2 text-foreground font-normal disabled:opacity-50"
          disabled={updateStatusMutation.isLoading}
          onClick={() => setShowResolveModal(true)}
        >
          {updateStatusMutation.isLoading ? 'Updating...' : 'Resolve'}
        </button>
      </>
    )
  }

  const renderContentRemainsActiveReason = () => {
    if (!report?.contentRemainActiveReason) {
      return null
    }

    if (report?.status !== CONTENT_REPORT_STATUS.resolved.key) {
      return null
    }

    return (
      <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg shadow-sm">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mt-0.5">
            <CheckCircleIcon className="w-4 h-4 text-green-600" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-green-800 mb-1">Content Remains Active</h4>
            <p className="text-xs text-green-700 mb-2">
              The reported <UserTypeDisplay userType={report.contentType} /> was reviewed and
              allowed to stay active
            </p>
            <div className="bg-background/60 border border-green-300 rounded p-3">
              <p className="text-xs font-medium text-green-800 mb-1">Admin Decision:</p>
              <p className="text-sm text-green-700 leading-relaxed">
                {report.contentRemainActiveReason}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!report) {
    return null
  }

  const reportInfoItems = [
    { label: 'Report ID', value: report.id },
    { label: 'Reason', value: report.reason },
    {
      label: 'Status',
      value: <ReportTicketStatus status={report.status} />,
    },
    {
      label: 'Reported By',
      value: (
        <>
          <span className="block">
            {report.reporter.name} - <UserTypeDisplay userType={report.reporter.userType} />
          </span>
          {report.reporter.phoneNumber && (
            <a className="block text-xs text-gray-400" href={`tel:${report.reporter.phoneNumber}`}>
              {report.reporter.phoneNumber}
            </a>
          )}
        </>
      ),
    },
    {
      label: 'Network Source',
      value: (
        <div>
          {report.ipAddress ? (
            <span className="block">{report.ipAddress}</span>
          ) : (
            <span className="text-gray-500">N/A</span>
          )}
          {report.userAgent && (
            <span className="block text-xs text-gray-400">{report.userAgent}</span>
          )}
        </div>
      ),
    },
    {
      label: 'Content',
      value: <UserTypeDisplay userType={report.contentType} />,
    },
    { label: 'Created At', value: formatTime(report.createdAt) },
  ]

  const reportActions = (
    <>
      <div className="flex gap-4">{renderActions()}</div>
      {renderContentRemainsActiveReason()}
    </>
  )

  return (
    <div className=" mx-auto ">
      <InfoGrid infoItems={reportInfoItems} actions={reportActions} />

      <DisplayReportedContent content={content} contentType={report.contentType} />

      <ConfirmModal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={async () => removeReportMutation.mutateAsync()}
        title="Dismiss Report?"
        message={
          <>
            Are you sure you want to dismiss this report? The{' '}
            <UserTypeDisplay userType={report.contentType} /> will remain active.
          </>
        }
      />
      <ResolveReportModal
        isOpen={showResolveModal}
        onClose={() => setShowResolveModal(false)}
        report={report}
        content={content}
      />
    </div>
  )
}
